import { Entity } from '../entity';
import { EntityStatus, EntitySubStatus } from '../entity.status/entity.status';
import { buildStatusStamp, EntityStatusStamp, findMissingStatusReason, resolveStatusIdentity, StatusIdentity, StatusSource, StatusStampOptions } from '../entity.status/entity.status.stamp';
import { EntityStatusType, Party } from '../entity.status/enum';
import { TASK_EVIDENCE_RESPONSE_FIELDS } from './task.evidence';
import { Task } from './task';
import { TaskStatus } from './task.enum';

/**
 * The one place that decides what a task's status means.
 *
 * Seven call sites answered "is it done" and "is it locked" independently, each assuming exactly one
 * terminal value, so E1-04's three new statuses had nowhere to attach.
 */

/** The system names the code stamps on the Task set (SP-13). Everything else is the tenant's to shape. */
export enum TaskSystemStatusName {
    Pending = 'Pending',
    Completed = 'Completed',
}

/** A Pending task takes evidence, and an exception for evidence it cannot get, and nothing else: its line is not authorised work yet (ST-13). */
export const PENDING_TASK_ALLOWED_FIELDS: ReadonlySet<string> = new Set(['_id', 'answered', 'additionalFiles', 'evidenceException' satisfies keyof Task, ...TASK_EVIDENCE_RESPONSE_FIELDS]);

/** What a locked task still accepts: the status itself and the completion metadata that travels with it. */
export const LOCKED_TASK_ALLOWED_FIELDS: ReadonlySet<string> = new Set(['_id', 'status', 'completedBy', 'completedOn']);

/** Resolved = the task holds no gate. The group decides, so a tenant status behaves like the ones it sits with. */
export function isTaskResolved(status: TaskStatus | string | null | undefined): boolean {

    return status === TaskStatus.Completed;
}

/** Same question asked of a stamped task: the group wins, the mirror answers for a task stamped before cutover. */
export function isStampedTaskResolved(task: Pick<Task, 'status' | 'taskStatus'> | null | undefined): boolean {

    const statusType = task?.taskStatus?.statusType;

    return statusType ? statusType === EntityStatusType.Inactive : isTaskResolved(task?.status);
}

/** An unstamped task predates the tenant's closed statuses, so its legacy Completed can only mean Completed. */
export function isStampedTaskCompleted(task: Pick<Task, 'status' | 'taskStatus'> | null | undefined): boolean {

    return task?.taskStatus
        ? task.taskStatus.systemName === TaskSystemStatusName.Completed
        : isTaskResolved(task?.status);
}

/** A resolved task refuses field edits until it is reopened. Superseded by `canEditInStatus` once stamped. */
export function isTaskLocked(task: Pick<Task, 'status'> | null | undefined): boolean {

    return isTaskResolved(task?.status);
}

/** Whether the party may no longer change the task's fields; a task stamped before the cutover has no status to read, so the legacy lock answers for it. */
export function isTaskLockedForParty(status: { editableBy?: Party[]; statusType?: EntityStatusType } | null | undefined, task: Pick<Task, 'status'> | null | undefined, party: Party): boolean {

    return status ? !canEditInStatus(status, party) : isTaskLocked(task);
}

/** A tenant-added status has no `editableBy`, so it keeps the pre-cutover rule rather than locking every party out. */
export function canEditInStatus(status: { editableBy?: Party[]; statusType?: EntityStatusType } | null | undefined, party: Party): boolean {

    if (!status) return false;

    return status.editableBy
        ? status.editableBy.includes(party)
        : status.statusType !== EntityStatusType.Inactive;
}

/**
 * The legacy value the stamp mirrors onto `Task.status`.
 *
 * Falls back to the group because a tenant-added status carries no `legacyStatus` and no admin should be
 * asked to type one; without the fallback the mirror empties on the first status a tenant adds.
 */
export function resolveTaskLegacyStatus(status: { legacyStatus?: any; statusType?: EntityStatusType }): TaskStatus {

    return status?.legacyStatus
        ?? (status?.statusType === EntityStatusType.Inactive ? TaskStatus.Completed : TaskStatus.Open);
}

/**
 * The one way a status reaches a task: the stamp, its legacy mirror and the completion metadata together.
 *
 * Nothing else may write `taskStatus` or `status`; a patch that sets the mirror directly is refused, which
 * is what keeps the two from drifting.
 */
export function stampTaskStatus(patch: Partial<Task>, status: EntityStatus | EntitySubStatus, options?: StatusStampOptions & { previous?: EntityStatusStamp }): void {

    patch.taskStatus = buildStatusStamp(status, options);
    patch.status = resolveTaskLegacyStatus(status);

    applyTaskCompletionStamp(patch, options?.setBy);
}

/** Why a request's own stamp cannot be stored, or undefined; its `systemName` decides whether the completion gate runs, so it must be the tenant's status of that id, not the body's word for it. */
export function findTaskStatusStampMismatch(before: Pick<Task, 'taskStatus'> | null | undefined, patch: Pick<Task, 'status' | 'taskStatus'> | null | undefined, statuses: readonly StatusSource[] | null | undefined): string | undefined {

    const stamp = patch?.taskStatus;
    if (!stamp) return undefined;

    // A status retired from the set is still the task's own, so resending it is not a lie.
    const stored = before?.taskStatus?._id === stamp._id ? before.taskStatus : undefined;
    const known = statuses?.find(status => status._id === stamp._id) ?? stored;
    if (!known) return `${stamp.name ?? stamp._id} is not one of this tenant's task statuses`;

    const identity = resolveStatusIdentity(known);
    const sameIdentity = (other: Pick<StatusIdentity, 'systemName' | 'statusType'>): boolean => (stamp.systemName ?? undefined) === (other.systemName ?? undefined) && (stamp.statusType ?? undefined) === (other.statusType ?? undefined);
    // The stored stamp resent unchanged is not a lie: Open gained its system name after tasks were stamped with it (WBS-12283).
    if (!sameIdentity(identity) && !(stored && sameIdentity(stored))) return `${stamp.name ?? stamp._id} does not match the tenant's status of that id; change the status as a whole`;
    if (patch.status && patch.status !== resolveTaskLegacyStatus(identity)) return `status ${patch.status} does not match ${known.name}; change the status as a whole`;

    return undefined;
}

/** True while the task belongs to a line nobody has approved yet. */
export function isPendingTask(task: Pick<Task, 'taskStatus'> | null | undefined): boolean {

    return task?.taskStatus?.systemName === TaskSystemStatusName.Pending;
}

/** The first non-evidence field in a patch against a Pending task, or undefined when it is all evidence. */
export function findPendingTaskViolation(task: Pick<Task, 'taskStatus'> | null | undefined, patch: object): string | undefined {

    if (!isPendingTask(task)) return undefined;

    return Object.keys(patch ?? {}).find(field => !PENDING_TASK_ALLOWED_FIELDS.has(field));
}

/**
 * The status a legacy `Open` / `Completed` value names.
 *
 * `Completed` resolves by system name; everything else lands in the set's default, because Open is
 * found through `isDefault` and a tenant may rename it.
 */
export function findStatusForLegacy<T extends { systemName?: string; isDefault?: boolean }>(statuses: T[], legacy: TaskStatus | string | null | undefined): T | undefined {

    return isTaskResolved(legacy)
        ? statuses?.find(status => status.systemName === TaskSystemStatusName.Completed)
        : statuses?.find(status => status.isDefault);
}

/**
 * Completion metadata follows the status: stamped entering a resolved status, cleared leaving one.
 *
 * A caller's own values win — the field app sends the time the technician pressed the button, which is
 * not the time the request landed.
 */
export function applyTaskCompletionStamp(patch: Partial<Task>, by: Entity): void {

    if (!patch?.status) return;

    if (!isTaskResolved(patch.status)) {
        patch.completedOn = null;
        patch.completedBy = null;
        return;
    }

    patch.completedOn ??= new Date();
    patch.completedBy ??= by;
}
